import {Component, DestroyRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {CreateWebsiteMode} from "../../models/create-website-option.model";
import {Subject} from "rxjs";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../../store";
import {HostingService} from "../../../../../websites/website/hosting/hosting.service";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {User} from "../../../../../user/user";
import {IWebsiteInstallationForm} from "../../models/website-install-data.model";
import {
    CreateWebsiteInstallBlankComponent
} from "../create-website-install-blank/create-website-install-blank.component";
import {
    CreateWebsiteInstallCloneComponent
} from "../create-website-install-clone/create-website-install-clone.component";
import {combineLatest} from "rxjs";
import {Website} from "../../../../../websites/website/website";
import {CreateWebsiteHelpersService} from "../../../../../services/local/create-website-helpers.service";
import {OptionRegion} from "../../../../../shared/models/option-region.model";
import {IWebsiteInstallCloneForm} from "../../models/website-clone-data.model";
import {UserSubscription, UserSubscriptionCategory} from "../../../../../user/user-subscription";
import {DbResponse} from "../../../../../shared/interceptors/db-response";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {GetResellerMainDomain} from "../../../../../store";

@Component({
    selector: 'db-create-website-install',
    templateUrl: './create-website-install.component.html',
    styleUrls: ['./create-website-install.component.scss'],
    standalone: false
})
export class CreateWebsiteInstallComponent implements OnInit, OnDestroy {

    @ViewChild('cloneWebsiteRef') cloneWebsiteRef: CreateWebsiteInstallCloneComponent;
    @ViewChild('createWebsiteRef') createWebsiteRef: CreateWebsiteInstallBlankComponent;

    @Output() readonly resized = new EventEmitter<[number, number]>();
    @Output() readonly createSubmitted = new EventEmitter<IWebsiteInstallationForm>();
    @Output() readonly cloneSubmitted = new EventEmitter<IWebsiteInstallCloneForm>();

    @Input() userSubscription: UserSubscription;

    public loading = false;
    public initialDataLoading = true;
    public createWebsiteMode: CreateWebsiteMode = 'single';
    public regions: OptionRegion[] = [];
    public suggestedSubdomain: string;
    public hostedWebsitesCount: number;
    public hostedWebsitesLimit: number;
    public cloneAvailableWebsites: Website[] = [];
    public canCreateMultisite = false;
    public isMobile = false;
    public isDesktop = false;
    public UserSubscriptionCategory = UserSubscriptionCategory;
    configHostname: any;
    private _destroyRef = inject(DestroyRef);
    private _user: User;

    private readonly _destroyStream$ = new Subject<boolean>();

    constructor(
        private readonly _store: Store<fromStore.DbStoreState>,
        private readonly _hostingService: HostingService,
        private readonly _createWebsiteHelpersService: CreateWebsiteHelpersService,
    ) {
    }

    ngOnInit() {
        this._store.dispatch(new GetResellerMainDomain());
        this.isMobile = window.innerWidth <= 991;
        this.isDesktop = window.innerWidth >= 992;
        this.resized.emit([300, 700]);
        this.getDomain();
        this._runInitialSubscriptions();
        this._getWebsites();
    }

    ngOnDestroy() {
        this._destroyStream$.next(true);
        this._destroyStream$.complete();
    }

    private getDomain() {
        this._store.select(fromStore.getMainDomain)
            .pipe(
                first(domain => !!domain),
                takeUntilDestroyed(this._destroyRef)
            ).subscribe(domain => {
            this.configHostname = domain?.main_domain;
        });
    }

    private _getWebsites(): void {
        this._store.select(fromStore.getWebsites)
            .pipe(
                first(websites => !!websites)
            )
            .subscribe(websites => {
                this.cloneAvailableWebsites = websites.filter(website => !website.parentId && !website.isNetwork && website.isActive && !!website.domain_hash);
                this.hostedWebsitesCount = websites.filter(website => !website.parentId && website.isActive && website.type === 'live' && !!website.domain_hash).length;
            });
    }

    private _runInitialSubscriptions(): void {
        this._store.select(fromStore.getUserLoaded)
            .pipe(
                filter((loaded: boolean) => loaded),
                switchMap(() => combineLatest(
                    this._store.select(fromStore.getUser),
                    this._store.select(fromStore.getUserSubscription),
                )),
                first()
            )
            .subscribe(([user, userSubscription]) => {
                this._user = user;
                this.userSubscription = userSubscription;
                this.canCreateMultisite = false;
                this.hostedWebsitesLimit = user && user.hosting_services && user.hosting_services.domain_count && userSubscription && userSubscription.plan ? parseInt(user.hosting_services.domain_count.parameter_value, 10) + user.additionalWebsiteCount : null;
                this._getInitialData(user.subscriptionId);
            });
    }

    private _getInitialData(subscriptionId): void {
        this._createWebsiteHelpersService.getWebsiteCreationInitialData(subscriptionId)
            .then(([suggestedSubDomain, regions]) => {
                this.suggestedSubdomain = suggestedSubDomain;
                const vpsRegion = {
                    label: this.userSubscription?.plan?.dedicatedVMInfo?.zoneName,
                    value: this.userSubscription?.plan?.dedicatedVMInfo?.vmZone,
                    isAvailable: true
                };
                this.regions = this.userSubscription?.isDedicatedVM ? [vpsRegion] : regions;
                this.initialDataLoading = false;
                this.changeModalSize();
                this.initialDataLoading = false;
            });
    }

    /** changes modal window size depends on installation states */
    public changeModalSize(): void {
        let height: number = 901;
        if (this.createWebsiteMode === 'multisite') {
            height += 114;
        }

        if (this.createWebsiteRef && this.createWebsiteRef.showDomainField) {
            height += 90;
        }
        this.resized.emit([height, 700]);
    }

    public changeMode(mode: CreateWebsiteMode): void {
        this.createWebsiteMode = mode;
        this.changeModalSize();
    }

    public createWebsite(): void {
        this.loading = true;
        this.createWebsiteRef.submit();
    }

    public cloneWebsite(): void {
        this.loading = true;
        this.cloneWebsiteRef.submit();
    }

    public installWebsite(): void {
        this.createWebsiteMode === 'clone' ? this.cloneWebsite() : this.createWebsite();
    }
}
